import React, { useState, useEffect, useRef } from 'react';
import { 
  Receipt, Plus, Search, Download, Trash2, Edit, Eye, 
  Calendar, User, Building, Loader2, FileSpreadsheet, 
  ChevronDown, TrendingUp, PiggyBank, X, Upload, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { ContactoCombobox } from '../components/ContactoCombobox';
import { Paperclip, FileText, Sparkles, Star, AlertTriangle, Copy } from 'lucide-react';
import CapturaFacturasDialog from '../components/CapturaFacturasDialog';
import { gastosAPI, contactosAPI, usuariosAPI, adjuntosAPI, fiscalidadAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Tipos de impuesto
const TIPOS_IMPUESTO = [
  { value: 'IVA 21%', label: 'IVA 21%', iva: 21, isp: false },
  { value: 'IVA 10%', label: 'IVA 10%', iva: 10, isp: false },
  { value: 'IVA 4%', label: 'IVA 4%', iva: 4, isp: false },
  { value: 'IVA 0%', label: 'Exento IVA', iva: 0, isp: false },
  { value: 'ISP 21%', label: 'ISP 21% · inversión del sujeto pasivo (art. 84.Uno.2º LIVA)', iva: 21, isp: true },
];

// El 0 es «falsy» en JavaScript: un gasto exento se reabría con el 21 % puesto de
// nuevo. La etiqueta se deduce del porcentaje realmente guardado.
const tipoImpuestoDesdeIva = (porcentaje, guardado, isp = false) => {
  if (isp) return 'ISP 21%';
  if (porcentaje === null || porcentaje === undefined || porcentaje === '') {
    return guardado || 'IVA 21%';
  }
  const tipo = TIPOS_IMPUESTO.find(t => !t.isp && Number(t.iva) === Number(porcentaje));
  return tipo ? tipo.value : (guardado || 'IVA 21%');
};

const formatDivisa = (amount, moneda) => {
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: moneda || 'EUR' })
      .format(amount || 0);
  } catch {
    return `${(amount || 0).toFixed(2)} ${moneda || ''}`;
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2 
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

const ControlesGastos = () => {
  const [gastos, setGastos] = useState([]);
  const [seleccion, setSeleccion] = useState([]);          // ids marcados para la acción en lote
  const [fechaLote, setFechaLote] = useState(new Date().toISOString().slice(0, 10));
  const [guardandoPago, setGuardandoPago] = useState(null);
  const [tiposGasto, setTiposGasto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Filtros de exportación
  const [exportFechaDesde, setExportFechaDesde] = useState('');
  const [exportFechaHasta, setExportFechaHasta] = useState('');
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isCapturaOpen, setIsCapturaOpen] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total_controles: 0,
    total_importes: 0,
    total_iva: 0,
    total_irpf: 0,
    total_coste: 0
  });
  
  // Contactos y usuarios para selects
  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [facturadores, setFacturadores] = useState([]);
  const [facturadoresConGastos, setFacturadoresConGastos] = useState([]);
  const [filtroFacturador, setFiltroFacturador] = useState('');
  const [facturadorInicializado, setFacturadorInicializado] = useState(false);
  const [entidades, setEntidades] = useState([]);
  const [monedas, setMonedas] = useState([{ codigo: 'EUR', nombre: 'Euro' }]);
  const [cargandoCambio, setCargandoCambio] = useState(false);
  const entidadTocada = useRef(false);
  
  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);

  // Adjuntos
  const [adjuntos, setAdjuntos] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadingAdjunto, setUploadingAdjunto] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form
  const [codigosAEAT, setCodigosAEAT] = useState([]);
  const [soloSinNumero, setSoloSinNumero] = useState(false);
  // Aviso de factura duplicada
  const [duplicados, setDuplicados] = useState([]);
  const [isDuplicadoOpen, setIsDuplicadoOpen] = useState(false);
  const [gruposDuplicados, setGruposDuplicados] = useState([]);
  const [isDuplicadosListaOpen, setIsDuplicadosListaOpen] = useState(false);
  const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
  const [form, setForm] = useState({
    tipo_gasto_id: '',
    tipo_gasto_nombre: '',
    proveedor_id: '',
    proveedor_nombre: '',
    cliente_id: '',
    cliente_nombre: '',
    facturador_id: '',
    facturador_nombre: '',
    numero_factura: '',
    fecha: new Date().toISOString().split('T')[0],
    descripcion: '',
    importe_base: 0,
    tipo_impuesto: 'IVA 21%',
    porcentaje_iva: 21,
    porcentaje_retencion: 0,
    deducible_iva: true,
    deducible_irpf: true,
    porcentaje_afectacion: 100,
    tiene_factura_completa: true,
    es_bien_inversion: false,
    codigo_aeat: '',
    // Entidad del despacho a la que se imputa el gasto
    facturador_entidad_id: '',
    facturador_entidad_nombre: '',
    // Divisa
    moneda: 'EUR',
    importe_divisa: 0,
    tipo_cambio: 1,
    fecha_tipo_cambio: '',
    origen_tipo_cambio: '',
    importe_pagado_eur: '',
    inversion_sujeto_pasivo: false
  });

  useEffect(() => {
    loadData();
  }, [currentPage, itemsPerPage, filtroFacturador, soloSinNumero]);

  useEffect(() => {
    fiscalidadAPI.getCodigosAEAT().then(r => setCodigosAEAT(r.data || [])).catch(() => setCodigosAEAT([]));
    // Cargar facturadores con gastos y fijar el usuario por defecto (Eduard Caula)
    gastosAPI.getFacturadores().then(r => {
      const lista = r.data?.facturadores || [];
      setFacturadoresConGastos(lista);
      if (!facturadorInicializado) {
        const def = r.data?.default_id;
        if (def) setFiltroFacturador(String(def));
        setFacturadorInicializado(true);
      }
    }).catch(() => {});
    // Entidades facturadoras del despacho (Configuración → Facturadores) y divisas
    gastosAPI.getFacturadoresEntidad()
      .then(r => setEntidades(r.data?.facturadores || []))
      .catch(() => setEntidades([]));
    gastosAPI.getMonedas()
      .then(r => setMonedas(r.data || []))
      .catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar gastos con paginación
      const facturadorParam = filtroFacturador ? Number(filtroFacturador) : undefined;
      const [gastosRes, tiposRes, statsRes, countRes] = await Promise.all([
        gastosAPI.getAll({ 
          skip: (currentPage - 1) * itemsPerPage, 
          limit: itemsPerPage,
          search: search || undefined,
          facturador_id: facturadorParam,
          sin_numero: soloSinNumero ? 1 : undefined
        }),
        gastosAPI.getTipos(),
        gastosAPI.getStats({ facturador_id: facturadorParam }),
        gastosAPI.getCount({ search: search || undefined, facturador_id: facturadorParam, sin_numero: soloSinNumero ? 1 : undefined })
      ]);
      
      setGastos(gastosRes.data);
      setTiposGasto(tiposRes.data);
      setStats(statsRes.data);
      setTotalCount(countRes.data.count);
      
      // Cargar contactos
      try {
        const contactosRes = await contactosAPI.getAll();
        const allContactos = contactosRes.data;
        setProveedores(allContactos.filter(c => c.tipo === 'proveedor'));
        setClientes(allContactos.filter(c => c.tipo === 'cliente'));
      } catch (err) {
        console.log('No se pudieron cargar contactos');
      }
      
      // Cargar usuarios como facturadores
      try {
        const usersRes = await usuariosAPI.getAll();
        setFacturadores(usersRes.data);
      } catch (err) {
        console.log('No se pudieron cargar usuarios');
      }
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    loadData();
  };

  const resetForm = () => {
    setForm({
      tipo_gasto_id: '',
      tipo_gasto_nombre: '',
      proveedor_id: '',
      proveedor_nombre: '',
      cliente_id: '',
      cliente_nombre: '',
      facturador_id: '',
      facturador_nombre: '',
      numero_factura: '',
      fecha: new Date().toISOString().split('T')[0],
      descripcion: '',
      importe_base: 0,
      tipo_impuesto: 'IVA 21%',
      porcentaje_iva: 21,
      porcentaje_retencion: 0,
      deducible_iva: true,
      deducible_irpf: true,
      porcentaje_afectacion: 100,
      tiene_factura_completa: true,
      es_bien_inversion: false,
      codigo_aeat: ''
    });
    setEditingGasto(null);
    setAdjuntos([]);
    setPendingFile(null);
  };

  // ---- Fecha de pago: es la que decide el trimestre del modelo 111 ----
  const hoyISO = () => new Date().toISOString().slice(0, 10);

  const guardarPago = async (gasto, fecha) => {
    setGuardandoPago(gasto.id);
    try {
      const { data } = await gastosAPI.marcarPago(gasto.id, fecha || null);
      setGastos((prev) => prev.map((g) => (g.id === gasto.id
        ? { ...g, fecha_pago: data.fecha_pago, estado: data.estado } : g)));
      toast({
        title: fecha ? 'Pago anotado' : 'Marcado como pendiente',
        description: fecha ? `${gasto.proveedor_nombre || 'Gasto'} · pagado el ${fecha.split('-').reverse().join('/')}` : undefined,
      });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar la fecha de pago', variant: 'destructive' });
    } finally {
      setGuardandoPago(null);
    }
  };

  const guardarPagosLote = async (modo) => {
    if (seleccion.length === 0) return;
    setGuardandoPago('lote');
    try {
      const { data } = await gastosAPI.marcarPagosLote(seleccion, modo, modo === 'fecha' ? fechaLote : null);
      toast({ title: 'Pagos actualizados', description: `${data.actualizados} gasto(s)` });
      setSeleccion([]);
      loadData();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.detail || 'No se pudieron actualizar los pagos',
        variant: 'destructive',
      });
    } finally {
      setGuardandoPago(null);
    }
  };

  const guardarClave111 = async (gasto, clave) => {
    try {
      const { data } = await gastosAPI.setClave111(gasto.id, clave);
      setGastos((prev) => prev.map((g) => (g.id === gasto.id
        ? { ...g, tipo_perceptor_111: data.tipo_perceptor_111 } : g)));
      toast({ title: 'Clave del modelo 111 guardada' });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar la clave', variant: 'destructive' });
    }
  };

  const alternarSeleccion = (id) => setSeleccion((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const seleccionarTodos = () => setSeleccion(
    seleccion.length === gastos.length ? [] : gastos.map((g) => g.id));

  const loadAdjuntos = async (gastoId) => {
    try {
      const res = await adjuntosAPI.getByGasto(gastoId);
      setAdjuntos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setAdjuntos([]);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (editingGasto) {
      // Subir de inmediato al gasto existente
      setUploadingAdjunto(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('tipo_adjunto', 'factura');
        await adjuntosAPI.uploadGasto(editingGasto.id, fd);
        toast({ title: 'Adjunto subido' });
        await loadAdjuntos(editingGasto.id);
      } catch (err) {
        toast({ title: 'Error', description: 'No se pudo subir el adjunto', variant: 'destructive' });
      } finally {
        setUploadingAdjunto(false);
      }
    } else {
      // Guardar para subir tras crear el gasto
      setPendingFile(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteAdjunto = async (adjuntoId) => {
    try {
      await adjuntosAPI.delete(adjuntoId);
      toast({ title: 'Adjunto eliminado' });
      if (editingGasto) await loadAdjuntos(editingGasto.id);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo eliminar el adjunto', variant: 'destructive' });
    }
  };

  // Abre la factura en una pestaña nueva usando /api/adjuntos/preview con el token en
  // la URL: es la ruta que el guardia v90 admite para esto, sirve los PDF locales en
  // línea y, cuando el archivo está en S3, redirige a la URL firmada (sin CORS, porque
  // es una navegación normal del navegador y no una petición XHR).
  const handleVerAdjunto = (adjuntoId) => {
    if (!adjuntoId) return;
    const token = localStorage.getItem('lexduo_token');
    const url = `${adjuntosAPI.getPreviewUrl(adjuntoId)}?token=${encodeURIComponent(token || '')}`;
    const ventana = window.open(url, '_blank', 'noopener,noreferrer');
    if (!ventana) {
      toast({
        title: 'El navegador ha bloqueado la ventana',
        description: 'Permite las ventanas emergentes de app.lexduo.cat para ver la factura.',
        variant: 'destructive'
      });
    }
  };

  const handleDownloadAdjunto = async (adjunto) => {
    try {
      const res = await adjuntosAPI.download(adjunto.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', adjunto.nombre || 'adjunto');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo descargar', variant: 'destructive' });
    }
  };

  const sugerirEntidad = async (proveedorId, proveedorNombre) => {
    try {
      const params = {};
      if (proveedorId) params.proveedor_id = proveedorId;
      else if (proveedorNombre) params.proveedor_nombre = proveedorNombre;
      const { data } = await gastosAPI.getEntidadSugerida(params);
      if (data?.id) {
        setForm(f => ({
          ...f,
          facturador_entidad_id: String(data.id),
          facturador_entidad_nombre: data.nombre
        }));
      } else if (data?.aviso) {
        toast({ title: 'Sin entidad facturadora', description: data.aviso });
      }
    } catch (err) {
      console.log('No se pudo sugerir la entidad facturadora');
    }
  };

  const consultarTipoCambio = async (moneda, fecha) => {
    if (!moneda || moneda === 'EUR') return;
    try {
      setCargandoCambio(true);
      const { data } = await gastosAPI.getTipoCambio({ moneda, fecha });
      setForm(f => ({
        ...f,
        tipo_cambio: data.unidades_por_eur,
        fecha_tipo_cambio: data.fecha_tipo,
        origen_tipo_cambio: data.origen
      }));
      toast({
        title: `1 EUR = ${Number(data.unidades_por_eur).toFixed(4)} ${data.moneda}`,
        description: `Cotización ${data.origen} de ${formatDate(data.fecha_tipo)}`
      });
    } catch (error) {
      const detalle = error?.response?.data?.detail;
      toast({
        title: 'No se pudo obtener el tipo de cambio',
        description: typeof detalle === 'string' ? detalle
          : 'Escríbelo a mano y quedará guardado como manual',
        variant: 'destructive'
      });
    } finally {
      setCargandoCambio(false);
    }
  };

  const handleMonedaChange = (moneda) => {
    const enDivisa = moneda !== 'EUR';
    setForm(f => ({
      ...f,
      moneda,
      importe_divisa: enDivisa ? (f.importe_divisa || f.importe_base || 0) : f.importe_divisa,
      tipo_cambio: enDivisa ? f.tipo_cambio : 1,
      fecha_tipo_cambio: enDivisa ? f.fecha_tipo_cambio : '',
      origen_tipo_cambio: enDivisa ? f.origen_tipo_cambio : '',
      importe_pagado_eur: enDivisa ? f.importe_pagado_eur : '',
      // Servicios de fuera de la UE: inversión del sujeto pasivo por defecto
      tipo_impuesto: enDivisa
        ? (f.tipo_impuesto === 'IVA 21%' ? 'ISP 21%' : f.tipo_impuesto)
        : (f.tipo_impuesto === 'ISP 21%' ? 'IVA 21%' : f.tipo_impuesto),
      inversion_sujeto_pasivo: enDivisa ? (f.tipo_impuesto === 'IVA 21%' ? true : f.inversion_sujeto_pasivo) : false
    }));
    if (enDivisa) consultarTipoCambio(moneda, form.fecha);
  };

  const handleEntidadChange = (entidadId) => {
    entidadTocada.current = true;
    const ent = entidades.find(f => String(f.id) === entidadId);
    setForm(f => ({
      ...f,
      facturador_entidad_id: entidadId,
      facturador_entidad_nombre: ent?.nombre || ''
    }));
  };

  const handleOpenNew = () => {
    resetForm();
    entidadTocada.current = false;
    setIsDialogOpen(true);
    sugerirEntidad(null, null);
  };

  const handleEdit = (gasto) => {
    entidadTocada.current = true;
    setForm({
      tipo_gasto_id: gasto.tipo_gasto_id?.toString() || '',
      tipo_gasto_nombre: gasto.tipo_gasto_nombre || '',
      proveedor_id: gasto.proveedor_id?.toString() || '',
      proveedor_nombre: gasto.proveedor_nombre || '',
      cliente_id: gasto.cliente_id?.toString() || '',
      cliente_nombre: gasto.cliente_nombre || '',
      facturador_id: gasto.facturador_id?.toString() || '',
      facturador_nombre: gasto.facturador_nombre || '',
      numero_factura: gasto.numero_factura || '',
      fecha: gasto.fecha || new Date().toISOString().split('T')[0],
      descripcion: gasto.descripcion || '',
      importe_base: gasto.importe_base ?? 0,
      tipo_impuesto: tipoImpuestoDesdeIva(gasto.porcentaje_iva, gasto.tipo_impuesto, gasto.inversion_sujeto_pasivo),
      porcentaje_iva: gasto.porcentaje_iva ?? 21,
      porcentaje_retencion: gasto.porcentaje_retencion ?? 0,
      deducible_iva: gasto.deducible_iva !== undefined ? gasto.deducible_iva : true,
      deducible_irpf: gasto.deducible_irpf !== undefined ? gasto.deducible_irpf : true,
      porcentaje_afectacion: gasto.porcentaje_afectacion !== undefined && gasto.porcentaje_afectacion !== null ? gasto.porcentaje_afectacion : 100,
      tiene_factura_completa: gasto.tiene_factura_completa !== undefined ? gasto.tiene_factura_completa : true,
      es_bien_inversion: gasto.es_bien_inversion || false,
      codigo_aeat: gasto.codigo_aeat || '',
      facturador_entidad_id: gasto.facturador_entidad_id ? String(gasto.facturador_entidad_id) : '',
      facturador_entidad_nombre: gasto.facturador_entidad_nombre || '',
      moneda: gasto.moneda || 'EUR',
      importe_divisa: gasto.importe_divisa || gasto.importe_base || 0,
      tipo_cambio: gasto.tipo_cambio || 1,
      fecha_tipo_cambio: gasto.fecha_tipo_cambio || '',
      origen_tipo_cambio: gasto.origen_tipo_cambio || '',
      importe_pagado_eur: gasto.importe_pagado_eur ?? '',
      inversion_sujeto_pasivo: !!gasto.inversion_sujeto_pasivo
    });
    setEditingGasto(gasto);
    setPendingFile(null);
    loadAdjuntos(gasto.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (gasto) => {
    if (!window.confirm(`¿Eliminar el gasto "${gasto.descripcion || 'sin descripción'}"?`)) return;
    
    try {
      const res = await gastosAPI.delete(gasto.id);
      toast({ title: 'Gasto eliminado', description: res.data?.message || undefined });
      loadData();
    } catch (error) {
      const detalle = error?.response?.data?.detail;
      toast({
        title: 'No se pudo eliminar',
        description: typeof detalle === 'string' ? detalle : 'No se pudo eliminar el gasto',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e, forzarDuplicado = false) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    
    try {
      const enDivisa = form.moneda && form.moneda !== 'EUR';
      const importeDivisa = parseFloat(form.importe_divisa) || 0;
      const tipoCambio = parseFloat(form.tipo_cambio) || 1;
      const data = {
        ...form,
        tipo_gasto_id: form.tipo_gasto_id ? parseInt(form.tipo_gasto_id) : null,
        proveedor_id: form.proveedor_id ? parseInt(form.proveedor_id) : null,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        facturador_id: form.facturador_id ? parseInt(form.facturador_id) : null,
        facturador_entidad_id: form.facturador_entidad_id ? parseInt(form.facturador_entidad_id) : null,
        importe_base: enDivisa
          ? Math.round((importeDivisa / tipoCambio) * 100) / 100
          : (parseFloat(form.importe_base) || 0),
        moneda: form.moneda || 'EUR',
        importe_divisa: enDivisa ? importeDivisa : (parseFloat(form.importe_base) || 0),
        tipo_cambio: enDivisa ? tipoCambio : 1,
        fecha_tipo_cambio: enDivisa ? (form.fecha_tipo_cambio || form.fecha) : null,
        origen_tipo_cambio: enDivisa ? (form.origen_tipo_cambio || 'manual') : null,
        importe_pagado_eur: form.importe_pagado_eur === '' || form.importe_pagado_eur === null
          ? null : parseFloat(form.importe_pagado_eur),
        inversion_sujeto_pasivo: !!form.inversion_sujeto_pasivo,
        porcentaje_iva: parseFloat(form.porcentaje_iva) || 0,
        porcentaje_retencion: parseFloat(form.porcentaje_retencion) || 0,
        porcentaje_afectacion: parseFloat(form.porcentaje_afectacion) || 0,
        codigo_aeat: form.codigo_aeat || null,
        numero_factura: (form.numero_factura || '').trim() || null,
        confirmar_duplicado: forzarDuplicado === true
      };

      // El Nº de factura es imprescindible para justificar el gasto ante la AEAT.
      // Solo se admite vacío si se declara que no hay factura completa (ticket).
      if (form.tiene_factura_completa && !data.numero_factura) {
        toast({
          title: 'Falta el Nº de factura',
          description: 'Es obligatorio para justificar el gasto fiscalmente. Si es un ticket o una factura simplificada, desactiva «Factura completa» en la clasificación fiscal.',
          variant: 'destructive'
        });
        setSaving(false);
        return;
      }
      
      if (editingGasto) {
        await gastosAPI.update(editingGasto.id, data);
        toast({ title: 'Gasto actualizado' });
      } else {
        const res = await gastosAPI.create(data);
        // Subir adjunto pendiente si existe
        if (pendingFile && res.data?.id) {
          try {
            const fd = new FormData();
            fd.append('file', pendingFile);
            fd.append('tipo_adjunto', 'factura');
            await adjuntosAPI.uploadGasto(res.data.id, fd);
          } catch (err) {
            toast({ title: 'Gasto creado, pero el adjunto falló', variant: 'destructive' });
          }
        }
        toast({ title: 'Gasto creado' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      const detalle = error?.response?.data?.detail;
      const dups = error?.response?.status === 409 ? (detalle?.duplicados || []) : [];
      if (dups.length > 0) {
        // No se guarda nada: el usuario decide si de verdad es otro gasto.
        setDuplicados(dups);
        setIsDuplicadoOpen(true);
      } else {
        toast({
          title: 'Error',
          description: typeof detalle === 'string' ? detalle : 'No se pudo guardar el gasto',
          variant: 'destructive'
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const cargarGruposDuplicados = async () => {
    setCargandoDuplicados(true);
    setIsDuplicadosListaOpen(true);
    try {
      const r = await gastosAPI.getGruposDuplicados();
      setGruposDuplicados(r.data?.grupos || []);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo revisar los duplicados', variant: 'destructive' });
    } finally {
      setCargandoDuplicados(false);
    }
  };

  const abrirGastoDuplicado = async (gastoId) => {
    try {
      const r = await gastosAPI.getById(gastoId);
      setIsDuplicadoOpen(false);
      setIsDuplicadosListaOpen(false);
      handleEdit(r.data);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo abrir el gasto', variant: 'destructive' });
    }
  };

  const borrarGastoDuplicado = async (gastoId) => {
    if (!window.confirm('¿Eliminar definitivamente este gasto duplicado?')) return;
    try {
      await gastosAPI.delete(gastoId);
      toast({ title: 'Gasto duplicado eliminado' });
      await cargarGruposDuplicados();
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar el gasto', variant: 'destructive' });
    }
  };

  const handleTipoGastoChange = (tipoId) => {
    const tipo = tiposGasto.find(t => t.id.toString() === tipoId);
    const nombre = tipo?.nombre || '';
    setForm(prev => ({
      ...prev,
      tipo_gasto_id: tipoId,
      tipo_gasto_nombre: nombre
    }));
    // Sugerir código AEAT si el usuario aún no eligió uno
    if (nombre) {
      fiscalidadAPI.sugerirCodigo(nombre).then(r => {
        setForm(prev => (prev.codigo_aeat ? prev : { ...prev, codigo_aeat: r.data?.codigo || '' }));
      }).catch(() => {});
    }
  };

  const handleProveedorChange = (provId) => {
    const prov = proveedores.find(p => p.id.toString() === provId);
    setForm({
      ...form,
      proveedor_id: provId,
      proveedor_nombre: prov?.nombre || ''
    });
    if (!entidadTocada.current) {
      sugerirEntidad(provId ? parseInt(provId) : null, prov?.nombre || null);
    }
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.id.toString() === clienteId);
    setForm({
      ...form,
      cliente_id: clienteId,
      cliente_nombre: cliente?.nombre || ''
    });
  };

  const handleFacturadorChange = (factId) => {
    const fact = facturadores.find(f => f.id.toString() === factId);
    setForm({
      ...form,
      facturador_id: factId,
      facturador_nombre: fact?.nombre || fact?.email || ''
    });
  };

  const handleTipoImpuestoChange = (tipoImpuesto) => {
    const tipo = TIPOS_IMPUESTO.find(t => t.value === tipoImpuesto);
    setForm({
      ...form,
      tipo_impuesto: tipoImpuesto,
      porcentaje_iva: tipo?.iva || 0,
      inversion_sujeto_pasivo: !!tipo?.isp
    });
  };

  // Calcular totales del formulario (siempre en euros)
  const calcularTotales = () => {
    const enDivisa = form.moneda && form.moneda !== 'EUR';
    const tipoCambio = parseFloat(form.tipo_cambio) || 1;
    const base = enDivisa
      ? (parseFloat(form.importe_divisa) || 0) / tipoCambio
      : (parseFloat(form.importe_base) || 0);
    const cuota = base * (parseFloat(form.porcentaje_iva) / 100);
    const iva = form.inversion_sujeto_pasivo ? 0 : cuota;
    const ivaAutorrepercutido = form.inversion_sujeto_pasivo ? cuota : 0;
    const retencion = base * (parseFloat(form.porcentaje_retencion) / 100);
    const pagado = form.importe_pagado_eur === '' || form.importe_pagado_eur === null
      ? null : parseFloat(form.importe_pagado_eur);
    const baseCoste = pagado ? pagado : base;
    const diferencia = pagado ? pagado - base : 0;
    const total = baseCoste + iva - retencion;
    return { base, iva, ivaAutorrepercutido, retencion, diferencia, total };
  };

  const totalesForm = calcularTotales();

  // Exportación
  const handleExport = async (format) => {
    try {
      const params = {};
      if (exportFechaDesde) params.fecha_desde = exportFechaDesde;
      if (exportFechaHasta) params.fecha_hasta = exportFechaHasta;
      
      let response;
      let filename;
      
      if (format === 'csv') {
        response = await gastosAPI.exportCSV(params);
        filename = `gastos_${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        response = await gastosAPI.exportExcel(params);
        filename = `gastos_${new Date().toISOString().split('T')[0]}.xls`;
      }
      
      // Descargar archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: `Exportado a ${format.toUpperCase()}` });
      setIsExportDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo exportar',
        variant: 'destructive'
      });
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading && gastos.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header con icono de enlace */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">📎</span>
        <h1 className="text-2xl font-bold text-blue-700">Controles de gastos</h1>
      </div>
      <p className="text-sm text-gray-600 mb-1">
        Estos son todos los elementos que se han creado en <strong>Quolaw</strong>.
      </p>
      <p className="text-xs text-gray-500 mb-4">
        Pulsa en los títulos de las columnas para ordenar y utiliza el buscador para filtrar.
      </p>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-t-4 border-t-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Controles de gastos</p>
            <p className="text-2xl font-bold text-blue-700">{stats.total_controles}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-gray-400">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Importes</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.total_importes)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-gray-400">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">IVA</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.total_iva)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-gray-400">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">IRPF</p>
            <p className="text-2xl font-bold">{formatCurrency(stats.total_irpf)}</p>
          </CardContent>
        </Card>
        <Card className="border-t-4 border-t-green-500">
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_coste)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium mb-2">Controles de gastos</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Busca en la tabla"
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>
            <div className="w-64" data-testid="filtro-facturador-wrap">
              <p className="text-sm font-medium mb-2">Usuario (facturador)</p>
              <div className="flex items-center gap-2">
                <Select
                  value={filtroFacturador || 'all'}
                  onValueChange={(v) => { setCurrentPage(1); setFiltroFacturador(v === 'all' ? '' : v); }}
                >
                  <SelectTrigger data-testid="filtro-facturador-select">
                    <SelectValue placeholder="Todos los usuarios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los usuarios</SelectItem>
                    {facturadoresConGastos.map(f => (
                      <SelectItem key={f.id} value={String(f.id)} data-testid={`filtro-facturador-opt-${f.id}`}>
                        {f.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filtroFacturador && (
                  <Button
                    variant="outline" size="icon" title="Fijar como usuario por defecto"
                    data-testid="fijar-facturador-default-btn"
                    onClick={async () => {
                      try {
                        await gastosAPI.setFacturadorDefault(Number(filtroFacturador));
                        toast({ title: 'Usuario por defecto actualizado' });
                      } catch { toast({ title: 'No se pudo guardar', variant: 'destructive' }); }
                    }}
                  >
                    <Star className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex gap-2 self-end">
              <Button
                variant="outline"
                className="bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600"
                onClick={() => setIsCapturaOpen(true)}
                data-testid="importar-facturas-ia-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                IMPORTAR CON IA
              </Button>
              <Button
                variant="outline"
                className={soloSinNumero
                  ? 'bg-red-600 text-white hover:bg-red-700 border-red-600'
                  : 'border-red-300 text-red-700 hover:bg-red-50'}
                onClick={() => { setCurrentPage(1); setSoloSinNumero(v => !v); }}
                data-testid="filtro-sin-numero-factura"
              >
                {soloSinNumero ? 'VER TODOS' : 'SIN Nº FACTURA'}
              </Button>
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={cargarGruposDuplicados}
                data-testid="revisar-duplicados-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                DUPLICADOS
              </Button>
              <Button 
                variant="outline" 
                className="bg-green-600 text-white hover:bg-green-700"
                onClick={() => setIsExportDialogOpen(true)}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                EXPORTAR
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleOpenNew}
              >
                <Plus className="w-4 h-4 mr-2" />
                NUEVO
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Acción en lote sobre los gastos marcados */}
      {seleccion.length > 0 && (
        <Card className="border-indigo-200 bg-indigo-50/60" data-testid="gastos-lote-barra">
          <CardContent className="py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-indigo-900" data-testid="gastos-lote-contador">
              {seleccion.length} gasto(s) seleccionado(s)
            </span>
            <Input type="date" value={fechaLote} onChange={(e) => setFechaLote(e.target.value)}
                   className="w-44 h-9" data-testid="gastos-lote-fecha" />
            <Button size="sm" onClick={() => guardarPagosLote('fecha')} disabled={guardandoPago === 'lote'}
                    data-testid="gastos-lote-pagar">
              Marcar como pagados en esa fecha
            </Button>
            <Button size="sm" variant="outline" onClick={() => guardarPagosLote('fecha_factura')}
                    disabled={guardandoPago === 'lote'} data-testid="gastos-lote-fecha-factura">
              Usar la fecha de cada factura
            </Button>
            <Button size="sm" variant="ghost" onClick={() => guardarPagosLote('pendiente')}
                    disabled={guardandoPago === 'lote'} data-testid="gastos-lote-pendiente">
              Dejar como pendientes
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSeleccion([])} className="ml-auto"
                    data-testid="gastos-lote-limpiar">
              Quitar selección
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="w-8 px-2 py-3">
                    <input type="checkbox" onChange={seleccionarTodos}
                           checked={gastos.length > 0 && seleccion.length === gastos.length}
                           title="Seleccionar todos los de esta página"
                           data-testid="gastos-seleccionar-todos" />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Tipo de gasto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Proveedor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Nº Factura
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Facturador
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100">
                    Fecha ↓
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Pago
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    Descripción
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                    Importes
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {gastos.length > 0 ? (
                  gastos.map((gasto) => (
                    <tr key={gasto.id} className="border-b hover:bg-gray-50">
                      <td className="px-2 py-3">
                        <input type="checkbox" checked={seleccion.includes(gasto.id)}
                               onChange={() => alternarSeleccion(gasto.id)}
                               data-testid={`gasto-sel-${gasto.id}`} />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {gasto.tipo_gasto_nombre || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-green-600" />
                          {gasto.proveedor_id ? (
                            <a
                              href={`/contactos/${gasto.proveedor_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                              title="Abrir la ficha del proveedor en una pestaña nueva"
                              data-testid={`gasto-proveedor-link-${gasto.id}`}
                            >
                              {gasto.proveedor_nombre || '-'}
                            </a>
                          ) : (
                            <span className="text-gray-700">{gasto.proveedor_nombre || '-'}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap" data-testid={`gasto-num-factura-${gasto.id}`}>
                        {gasto.numero_factura ? (
                          <span className="font-mono text-xs">{gasto.numero_factura}</span>
                        ) : (
                          <span className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                            Falta
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {gasto.cliente_nombre ? (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-green-600" />
                            <span>{gasto.cliente_nombre}</span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {gasto.facturador_nombre || gasto.facturador_entidad_nombre ? (
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-green-600" />
                            <div>
                              <div>{gasto.facturador_nombre || gasto.facturador_entidad_nombre}</div>
                              {gasto.facturador_entidad_nombre && gasto.facturador_nombre && (
                                <div className="text-xs text-gray-500" data-testid={`gasto-entidad-${gasto.id}`}>
                                  {gasto.facturador_entidad_nombre}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {formatDate(gasto.fecha)}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap" data-testid={`gasto-pago-${gasto.id}`}>
                        <input
                          type="date"
                          value={gasto.fecha_pago ? String(gasto.fecha_pago).slice(0, 10) : ''}
                          onChange={(e) => guardarPago(gasto, e.target.value)}
                          disabled={guardandoPago === gasto.id}
                          className={`h-8 px-2 rounded border text-xs ${gasto.fecha_pago ? 'border-gray-200 text-gray-700' : 'border-amber-300 bg-amber-50 text-amber-800'}`}
                          title={gasto.fecha_pago ? 'Fecha de pago' : 'Pendiente de pago'}
                          data-testid={`gasto-fecha-pago-${gasto.id}`}
                        />
                        <div className="flex gap-1 mt-1">
                          {!gasto.fecha_pago && (
                            <>
                              <button type="button" onClick={() => guardarPago(gasto, hoyISO())}
                                      className="text-[11px] text-indigo-600 hover:underline"
                                      data-testid={`gasto-pagar-hoy-${gasto.id}`}>hoy</button>
                              <button type="button" onClick={() => guardarPago(gasto, String(gasto.fecha).slice(0, 10))}
                                      className="text-[11px] text-indigo-600 hover:underline"
                                      data-testid={`gasto-pagar-factura-${gasto.id}`}>fecha factura</button>
                            </>
                          )}
                          {gasto.fecha_pago && (
                            <button type="button" onClick={() => guardarPago(gasto, null)}
                                    className="text-[11px] text-gray-400 hover:underline"
                                    data-testid={`gasto-pago-quitar-${gasto.id}`}>quitar</button>
                          )}
                        </div>
                        {Number(gasto.porcentaje_retencion) > 0 && (
                          <select
                            value={gasto.tipo_perceptor_111 || ''}
                            onChange={(e) => guardarClave111(gasto, e.target.value)}
                            className="mt-1 h-7 text-[11px] border border-gray-200 rounded px-1 w-full"
                            title="Clave de perceptor del modelo 111 para esta factura"
                            data-testid={`gasto-clave111-${gasto.id}`}
                          >
                            <option value="">111: según la ficha del proveedor</option>
                            <option value="actividad_economica">111: profesional (07-09)</option>
                            <option value="trabajo">111: trabajo / nómina (01-03)</option>
                            <option value="premios">111: premios (13-15)</option>
                            <option value="forestales">111: aprov. forestales (19-21)</option>
                            <option value="imagen">111: derechos de imagen (25-27)</option>
                            <option value="alquiler_inmueble">111: alquiler → modelo 115</option>
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm max-w-[300px] truncate">
                        {gasto.descripcion || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        <div data-testid={`gasto-coste-${gasto.id}`}>{formatCurrency(gasto.coste_total)}</div>
                        {gasto.moneda && gasto.moneda !== 'EUR' && (
                          <div className="text-xs text-gray-500" data-testid={`gasto-divisa-${gasto.id}`}>
                            {formatDivisa(gasto.importe_divisa, gasto.moneda)}
                            {gasto.tipo_cambio ? ` · 1 € = ${Number(gasto.tipo_cambio).toFixed(4).replace('.', ',')}` : ''}
                          </div>
                        )}
                        {gasto.inversion_sujeto_pasivo && (
                          <div className="text-xs text-amber-600" data-testid={`gasto-isp-${gasto.id}`}>
                            ISP {formatCurrency(gasto.iva_autorrepercutido)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {gasto.adjunto_id ? (
                            <button
                              className="p-1 hover:bg-indigo-100 rounded"
                              title="Ver la factura (PDF)"
                              onClick={() => handleVerAdjunto(gasto.adjunto_id)}
                              data-testid={`gasto-ver-factura-${gasto.id}`}
                            >
                              <Eye className="w-4 h-4 text-indigo-600" />
                            </button>
                          ) : (
                            <button
                              className="p-1 rounded cursor-not-allowed"
                              title={gasto.tiene_adjunto
                                ? 'Marcado con adjunto, pero no hay archivo guardado'
                                : 'Sin factura adjunta: añádela editando el gasto'}
                              disabled
                              data-testid={`gasto-sin-factura-${gasto.id}`}
                            >
                              <Eye className="w-4 h-4 text-gray-300" />
                            </button>
                          )}
                          <button 
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Editar"
                            onClick={() => handleEdit(gasto)}
                          >
                            <Edit className="w-4 h-4 text-gray-600" />
                          </button>
                          <button 
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Eliminar"
                            onClick={() => handleDelete(gasto)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="px-4 py-12 text-center text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">No se encontraron gastos</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Items por página</span>
              <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} de {totalCount}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                ←
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                →
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Nuevo/Editar Gasto */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGasto ? 'Editar control de gastos' : 'Nuevo control de gastos'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Tipo de gasto y Fecha */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de gasto</Label>
                  <Select value={form.tipo_gasto_id} onValueChange={handleTipoGastoChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo de gasto" />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposGasto.map(tipo => (
                        <SelectItem key={tipo.id} value={tipo.id.toString()}>
                          {tipo.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input 
                      type="date" 
                      className="pl-10"
                      value={form.fecha}
                      onChange={(e) => {
                        setForm({...form, fecha: e.target.value});
                        if (form.moneda !== 'EUR') consultarTipoCambio(form.moneda, e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Nº de factura del proveedor — imprescindible fiscalmente */}
              <div>
                <Label>
                  Nº de factura{form.tiene_factura_completa ? <span className="text-red-600"> *</span> : ''}
                </Label>
                <Input
                  value={form.numero_factura}
                  onChange={(e) => setForm({ ...form, numero_factura: e.target.value })}
                  placeholder="Tal como aparece en la factura (p. ej. 26F3001F00113205)"
                  className={form.tiene_factura_completa && !(form.numero_factura || '').trim() ? 'border-red-400' : ''}
                  data-testid="gasto-numero-factura"
                />
                {form.tiene_factura_completa && !(form.numero_factura || '').trim() && (
                  <p className="text-xs text-red-600 mt-1" data-testid="gasto-numero-factura-aviso">
                    Obligatorio para justificar el gasto. Si es un ticket, desactiva «Factura completa».
                  </p>
                )}
              </div>

              {/* Proveedores */}
              <div>
                <Label>Proveedores</Label>
                <ContactoCombobox
                  tipo="proveedor"
                  value={form.proveedor_id}
                  nombreValue={form.proveedor_nombre}
                  onChange={(id, nombre) => setForm({ ...form, proveedor_id: id, proveedor_nombre: nombre })}
                  testId="gasto-proveedor"
                />
              </div>

              {/* Clientes */}
              <div>
                <Label>Clientes</Label>
                <ContactoCombobox
                  tipo="cliente"
                  value={form.cliente_id}
                  nombreValue={form.cliente_nombre}
                  onChange={(id, nombre) => setForm({ ...form, cliente_id: id, cliente_nombre: nombre })}
                  testId="gasto-cliente"
                />
              </div>

              {/* Facturadores */}
              <div>
                <Label>Usuario que tramita</Label>
                <Select value={form.facturador_id} onValueChange={handleFacturadorChange}>
                  <SelectTrigger data-testid="gasto-usuario-tramita-select">
                    <SelectValue placeholder="Usuario que tramita" />
                  </SelectTrigger>
                  <SelectContent>
                    {facturadores.map(fact => (
                      <SelectItem key={fact.id} value={fact.id.toString()}>
                        {fact.nombre || fact.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Entidad facturadora del despacho */}
              <div>
                <Label>Facturador del despacho (a quién se imputa)</Label>
                <Select value={form.facturador_entidad_id} onValueChange={handleEntidadChange}>
                  <SelectTrigger data-testid="gasto-entidad-select">
                    <SelectValue placeholder="Entidad facturadora" />
                  </SelectTrigger>
                  <SelectContent>
                    {entidades.map(ent => (
                      <SelectItem key={ent.id} value={String(ent.id)}>
                        {ent.nombre}{ent.nif ? ` · ${ent.nif}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {entidades.length === 0 ? (
                  <p className="text-xs text-amber-600 mt-1" data-testid="gasto-sin-entidades">
                    No hay facturadores activos. Añádelos en Configuración → Facturadores.
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Se propone el último usado con este proveedor y, si no hay, el predeterminado.
                  </p>
                )}
              </div>

              {/* Descripción */}
              <div>
                <Label>Descripción</Label>
                <Input 
                  value={form.descripcion}
                  onChange={(e) => setForm({...form, descripcion: e.target.value})}
                  placeholder="Descripción"
                />
              </div>

              {/* Moneda e importe de la factura */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Moneda de la factura</Label>
                  <Select value={form.moneda} onValueChange={handleMonedaChange}>
                    <SelectTrigger data-testid="gasto-moneda-select">
                      <SelectValue placeholder="Moneda" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {monedas.map(m => (
                        <SelectItem key={m.codigo} value={m.codigo}>
                          {m.codigo} · {m.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{form.moneda === 'EUR' ? 'Importes | Importe' : `Importe en ${form.moneda}`}</Label>
                  <div className="relative">
                    <Input 
                      type="number"
                      step="0.01"
                      value={form.moneda === 'EUR' ? form.importe_base : form.importe_divisa}
                      onChange={(e) => setForm(form.moneda === 'EUR'
                        ? { ...form, importe_base: e.target.value }
                        : { ...form, importe_divisa: e.target.value })}
                      className="pr-10"
                      data-testid="gasto-importe-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      {form.moneda === 'EUR' ? '€' : form.moneda}
                    </span>
                  </div>
                </div>
              </div>

              {/* Conversión a euros (solo divisa extranjera) */}
              {form.moneda !== 'EUR' && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-3" data-testid="gasto-bloque-cambio">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Tipo de cambio (1 EUR = … {form.moneda})</Label>
                      <Input
                        type="number"
                        step="0.000001"
                        value={form.tipo_cambio}
                        onChange={(e) => setForm({ ...form, tipo_cambio: e.target.value, origen_tipo_cambio: 'manual' })}
                        data-testid="gasto-tipo-cambio-input"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        disabled={cargandoCambio}
                        onClick={() => consultarTipoCambio(form.moneda, form.fecha)}
                        data-testid="gasto-btn-tipo-cambio-bce"
                      >
                        {cargandoCambio && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        TIPO DEL BCE
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600" data-testid="gasto-cambio-info">
                    {form.origen_tipo_cambio
                      ? `Cotización ${form.origen_tipo_cambio}${form.fecha_tipo_cambio ? ` de ${formatDate(form.fecha_tipo_cambio)}` : ''} · `
                      : ''}
                    Base imponible: {formatCurrency(totalesForm.base)}
                  </p>
                  <div>
                    <Label className="text-xs">Cargo real del banco en euros (opcional, comisiones incluidas)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Lo que te cargó la tarjeta"
                        value={form.importe_pagado_eur}
                        onChange={(e) => setForm({ ...form, importe_pagado_eur: e.target.value })}
                        className="pr-8"
                        data-testid="gasto-pagado-eur-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                    </div>
                    {form.importe_pagado_eur !== '' && form.importe_pagado_eur !== null && (
                      <p className="text-xs text-gray-600 mt-1" data-testid="gasto-diferencia-cambio">
                        Diferencia de cambio: {formatCurrency(totalesForm.diferencia)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Impuesto */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de impuesto</Label>
                  <Select value={form.tipo_impuesto} onValueChange={handleTipoImpuestoChange}>
                    <SelectTrigger data-testid="gasto-tipo-impuesto-select">
                      <SelectValue placeholder="Tipo de impuesto" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_IMPUESTO.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{form.inversion_sujeto_pasivo ? 'IVA autorrepercutido' : 'IVA'}</Label>
                  <Input
                    type="text"
                    readOnly
                    className="bg-gray-50 border-dashed"
                    value={formatCurrency(form.inversion_sujeto_pasivo ? totalesForm.ivaAutorrepercutido : totalesForm.iva)}
                    data-testid="gasto-iva-calculado"
                  />
                </div>
              </div>

              {form.inversion_sujeto_pasivo && (
                <p className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="gasto-aviso-isp">
                  Inversión del sujeto pasivo (art. 84.Uno.2º LIVA): el proveedor no repercute IVA.
                  Se autorrepercute el {form.porcentaje_iva}% en el modelo 303 (casillas 12-13) y se
                  deduce la misma cuota (casillas 28-29), de modo que el coste del gasto es la base,
                  no base + IVA. No se presenta modelo 349 porque EE. UU. no es país de la UE.
                </p>
              )}

              {/* Retención y Coste total */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Retención</Label>
                  <div className="relative">
                    <Input 
                      type="number"
                      step="0.01"
                      value={form.porcentaje_retencion}
                      onChange={(e) => setForm({...form, porcentaje_retencion: e.target.value})}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                  </div>
                </div>
                <div>
                  <Label>Coste total</Label>
                  <div className="relative">
                    <Input 
                      type="text"
                      value={totalesForm.total.toFixed(2)}
                      readOnly
                      className="pr-8 bg-gray-50 border-dashed"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                  </div>
                </div>
              </div>

              {/* Clasificación fiscal (Módulo Fiscalidad) */}
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-3" data-testid="gasto-fiscal-section">
                <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Clasificación fiscal</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Código AEAT</Label>
                    <Select value={form.codigo_aeat} onValueChange={(v) => setForm({ ...form, codigo_aeat: v })}>
                      <SelectTrigger data-testid="gasto-codigo-aeat"><SelectValue placeholder="Código AEAT" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {codigosAEAT.filter(c => c.tipo === 'gasto').map(c => (
                          <SelectItem key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>% Afectación a la actividad</Label>
                    <div className="relative">
                      <Input
                        type="number" step="1" min="0" max="100"
                        value={form.porcentaje_afectacion}
                        onChange={(e) => setForm({ ...form, porcentaje_afectacion: e.target.value })}
                        className="pr-8"
                        data-testid="gasto-afectacion"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <label className="flex items-center justify-between text-sm">
                    <span>IVA deducible</span>
                    <Switch checked={form.deducible_iva} onCheckedChange={(v) => setForm({ ...form, deducible_iva: v })} data-testid="gasto-deducible-iva" />
                  </label>
                  <label className="flex items-center justify-between text-sm">
                    <span>Deducible en IRPF</span>
                    <Switch checked={form.deducible_irpf} onCheckedChange={(v) => setForm({ ...form, deducible_irpf: v })} data-testid="gasto-deducible-irpf" />
                  </label>
                  <label className="flex items-center justify-between text-sm">
                    <span>Factura completa</span>
                    <Switch checked={form.tiene_factura_completa} onCheckedChange={(v) => setForm({ ...form, tiene_factura_completa: v })} data-testid="gasto-factura-completa" />
                  </label>
                  <label className="flex items-center justify-between text-sm">
                    <span>Bien de inversión</span>
                    <Switch checked={form.es_bien_inversion} onCheckedChange={(v) => setForm({ ...form, es_bien_inversion: v })} data-testid="gasto-bien-inversion" />
                  </label>
                </div>
                {form.es_bien_inversion && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Este gasto se podrá dar de alta como activo amortizable en <strong>Fiscalidad → Bienes de inversión</strong>.
                  </p>
                )}
              </div>

              {/* Adjuntos (factura del proveedor) */}
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="gasto-adjunto-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAdjunto}
                  data-testid="gasto-anadir-adjunto"
                >
                  {uploadingAdjunto ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}
                  AÑADIR ADJUNTO
                </Button>

                {/* Archivo pendiente (modo nuevo) */}
                {pendingFile && !editingGasto && (
                  <div className="flex items-center justify-between text-sm bg-gray-50 border rounded px-3 py-2" data-testid="gasto-adjunto-pendiente">
                    <span className="flex items-center gap-2 truncate">
                      <FileText className="w-4 h-4 text-blue-600" /> {pendingFile.name}
                    </span>
                    <button type="button" className="text-red-500 hover:text-red-700" onClick={() => setPendingFile(null)}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Lista de adjuntos (modo edición) */}
                {editingGasto && adjuntos.length > 0 && (
                  <div className="space-y-1" data-testid="gasto-adjuntos-lista">
                    {adjuntos.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-sm bg-gray-50 border rounded px-3 py-2">
                        <span className="flex items-center gap-2 truncate">
                          <FileText className="w-4 h-4 text-blue-600" /> {a.nombre}
                        </span>
                        <div className="flex items-center gap-2">
                          <button type="button" className="text-indigo-600 hover:text-indigo-800" title="Ver en una pestaña nueva" onClick={() => handleVerAdjunto(a.id)} data-testid={`gasto-ver-adjunto-${a.id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                          <button type="button" className="text-blue-600 hover:text-blue-800" title="Descargar" onClick={() => handleDownloadAdjunto(a)}>
                            <Download className="w-4 h-4" />
                          </button>
                          <button type="button" className="text-red-500 hover:text-red-700" title="Eliminar" onClick={() => handleDeleteAdjunto(a.id)}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
                CANCELAR
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                GUARDAR
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Importar facturas con IA */}
      <CapturaFacturasDialog
        open={isCapturaOpen}
        onOpenChange={setIsCapturaOpen}
        onConfirmado={loadData}
      />

      {/* Aviso de factura duplicada: no se ha guardado nada todavía */}
      <Dialog open={isDuplicadoOpen} onOpenChange={setIsDuplicadoOpen}>
        <DialogContent className="max-w-2xl" data-testid="aviso-duplicado-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Posible factura duplicada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              No se ha guardado nada. Ya hay {duplicados.length === 1 ? 'un gasto' : `${duplicados.length} gastos`} con
              este nº de factura y este proveedor:
            </p>
            <div className="space-y-2">
              {duplicados.map((d) => (
                <div
                  key={d.id}
                  className="border border-red-200 bg-red-50 rounded-md p-3 text-sm"
                  data-testid={`duplicado-existente-${d.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span className="font-mono font-semibold">{d.numero_factura || 'sin nº'}</span>
                      <span className="text-gray-700"> · {d.proveedor_nombre || 'sin proveedor'}</span>
                      <div className="text-xs text-gray-600 mt-1">
                        {formatDate(d.fecha)} · base {formatCurrency(d.importe_base)} · total {formatCurrency(d.coste_total)}
                        {d.coincidencia === 'total' && (
                          <span className="ml-2 text-red-700 font-medium">mismo importe</span>
                        )}
                      </div>
                      {d.descripcion && <div className="text-xs text-gray-500 mt-1">{d.descripcion}</div>}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => abrirGastoDuplicado(d.id)}
                      data-testid={`duplicado-ver-${d.id}`}
                    >
                      Ver el gasto
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              Si se trata de otra factura distinta con el mismo número, puedes guardarla igualmente.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDuplicadoOpen(false)}
              data-testid="duplicado-no-guardar-btn"
            >
              No guardar
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => { setIsDuplicadoOpen(false); handleSubmit(null, true); }}
              data-testid="duplicado-guardar-igualmente-btn"
            >
              Guardar igualmente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revisión de duplicados ya grabados */}
      <Dialog open={isDuplicadosListaOpen} onOpenChange={setIsDuplicadosListaOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="lista-duplicados-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-amber-600" />
              Gastos duplicados
            </DialogTitle>
          </DialogHeader>
          {cargandoDuplicados ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Revisando el histórico…
            </div>
          ) : gruposDuplicados.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-10" data-testid="sin-duplicados">
              No hay gastos con el mismo nº de factura y proveedor. Todo correcto.
            </p>
          ) : (
            <div className="space-y-4">
              {gruposDuplicados.map((g, idx) => (
                <div key={`${g.numero_normalizado}-${idx}`} className="border rounded-lg p-3" data-testid={`grupo-duplicado-${idx}`}>
                  <div className="text-sm font-medium mb-2">
                    <span className="font-mono">{g.numero_factura}</span>
                    <span className="text-gray-600"> · {g.proveedor_nombre || 'sin proveedor'}</span>
                    <Badge className="ml-2 bg-amber-100 text-amber-800">{g.cuantos} veces</Badge>
                  </div>
                  <div className="space-y-1">
                    {g.gastos.map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-2 text-sm border-t pt-2">
                        <div>
                          <span className="text-gray-700">{formatDate(d.fecha)}</span>
                          <span className="text-gray-500"> · base {formatCurrency(d.importe_base)} · total {formatCurrency(d.coste_total)}</span>
                          {d.descripcion && <div className="text-xs text-gray-500">{d.descripcion}</div>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => abrirGastoDuplicado(d.id)} data-testid={`grupo-editar-${d.id}`}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => borrarGastoDuplicado(d.id)} data-testid={`grupo-borrar-${d.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDuplicadosListaOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Exportar */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar Gastos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Selecciona el rango de fechas para exportar los gastos.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha desde</Label>
                <Input 
                  type="date"
                  value={exportFechaDesde}
                  onChange={(e) => setExportFechaDesde(e.target.value)}
                />
              </div>
              <div>
                <Label>Fecha hasta</Label>
                <Input 
                  type="date"
                  value={exportFechaHasta}
                  onChange={(e) => setExportFechaHasta(e.target.value)}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Deja vacío para exportar todos los gastos.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleExport('csv')}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <Button 
              onClick={() => handleExport('excel')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Exportar XLS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ControlesGastos;
